import { Button, Card, Center, Loader } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconArrowBackUp, IconEdit, IconTrash } from '@tabler/icons-react';
import PageContent from '../../../../components/core/PageContent/PageContent';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import { Permission } from '../../../../models/core/departments.type';
import { ResidueType } from '../../../../models/core/residues.type';
import residuesService from '../../../../services/core/residues.service';
import { Feature } from '../../../../utils/constants.utils';
import DataView from './components/DataView';

type Result = {
  loading: boolean;
  data: ResidueType | null;
};

function ResidueView() {
  const navigate = useNavigate();
  const location: any = useLocation();
  const modals = useModals();
  const { idResiduo } = useParams();

  const [result, setResult] = useState<Result>({
    loading: location?.state?.residue === undefined,
    data: location?.state?.residue || null,
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      let data: ResidueType | null = null;
      try {
        data = await residuesService.select({ idResiduo: Number(idResiduo) });
      } catch (error: any) {
        showNotification({
          title: 'Resíduo - Visualizar',
          message: error?.isBusinessException ? error.description : `Não foi possível carregar o resíduo.`,
          color: 'red',
        });
        navigate(`/${error?.statusCode || '500'}`, {
          replace: true,
        });
      } finally {
        setResult({ loading: false, data });
      }
    };
    if (result.data === null) {
      fetchData();
    }
  }, [idResiduo, navigate, result]);

  const handleAction = async () => {
    try {
      setResult({ loading: false, data: result.data });

      setDeleting(true);
      await residuesService.delete({ idResiduo: Number(idResiduo) });
      navigate('/residues');

      showNotification({
        title: 'Resíduo - Visualizar',
        message: `Resíduo excluído com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Resíduo - Visualizar',
        message: error?.isBusinessException ? error.description : `Não foi possível excluir o resíduo.`,
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  };

  const showModal = () =>
    modals.openConfirmModal({
      title: `Gostaria de excluir esse resíduo?`,
      size: 'sm',
      closeOnClickOutside: false,
      closeOnEscape: false,
      onConfirm: () => {
        handleAction();
      },
    });

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Residue}
        title="Resíduo - Visualizar"
        description="Visualize os detalhes de um resíduo."
        buttons={[
          <Button
            key="Voltar"
            color="secondary"
            leftSection={<IconArrowBackUp size={18} />}
            onClick={() => {
              navigate(-1);
            }}
            disabled={deleting || result.loading}
          >
            Voltar
          </Button>,
          <Button
            key="Excluir"
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={() => {
              showModal();
            }}
            disabled={deleting || result.loading}
            loading={deleting}
            data-permission={Permission.ResidueDelete}
          >
            Excluir
          </Button>,
          <Button
            key="Editar"
            color="accent"
            leftSection={<IconEdit size={18} />}
            onClick={() => {
              navigate(`/residues/${result?.data?.idResiduo}/edit`, {
                state: {
                  residue: result.data,
                },
              });
            }}
            disabled={deleting || result.loading}
            data-permission={Permission.ResidueEdit}
          >
            Editar
          </Button>,
        ]}
      />
      {result.data === null ? (
        <Center>
          <Loader size="xl" />
        </Center>
      ) : (
        <PageContent>
          <DataView data={result.data} />
        </PageContent>
      )}
    </Card>
  );
}

export default ResidueView;
